// src/pages/AllAffiliateStats.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "../services/api";
import { format } from "date-fns";

const fmtGBP = (n) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" })
    .format(Number(n || 0));

export default function AllAffiliateStats() {
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [stats, setStats] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [loadingStats, setLoadingStats] = useState(false);
  const [error, setError] = useState("");
  const [range, setRange] = useState("6"); // "6" | "12" | "all"

  useEffect(() => {
    (async () => {
      setLoadingUsers(true);
      setError("");
      try {
        const res = await axios.get("/api/master/affiliates?page=0");
        const list = res.data?.content ?? res.data ?? [];
        setUsers(list);
        if (list.length > 0) setSelectedUserId(String(list[0].id));
      } catch {
        setError("Failed to load users.");
      } finally {
        setLoadingUsers(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!selectedUserId) return;
    (async () => {
      setLoadingStats(true);
      setError("");
      setStats([]);
      try {
        const res = await axios.get(`/api/master/affiliates/${selectedUserId}/sales-history`);
        const list = Array.isArray(res.data) ? res.data : [];
        setStats(list);
      } catch {
        setError("Failed to load stats.");
      } finally {
        setLoadingStats(false);
      }
    })();
  }, [selectedUserId]);

  const visibleStats = useMemo(() => {
    const ordered = [...stats].sort(
      (a, b) => new Date(String(b.month) + "-01") - new Date(String(a.month) + "-01")
    );
    if (range === "all") return ordered;
    const n = Number(range);
    return ordered.slice(0, n);
  }, [stats, range]);

  const totals = useMemo(() => {
    const sum = (k) => visibleStats.reduce((a, b) => a + Number(b?.[k] || 0), 0);
    return {
      totalTickets: sum("totalTickets"),
      standardTickets: sum("standardTickets"),
      vipTickets: sum("vipTickets"),
      commissionEarned: sum("commissionEarned"),
      bonusEarned: sum("bonusEarned"),
    };
  }, [visibleStats]);

  return (
    <div className="page space-y-6">
      {/* Header + Filters */}
      <section className="card relative z-10 overflow-visible">
        <div className="md:flex md:items-end md:justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl md:text-4xl">Affiliate / Admin Sales</h1>
            <p className="mt-1 text-slate-300">
              {visibleStats.length > 0
                ? `Showing ${range === "all" ? visibleStats.length : range} month${visibleStats.length === 1 ? "" : "s"} for the selected user.`
                : "Select a user to view performance."}
            </p>
          </div>

          <div className="relative z-20 flex flex-col sm:flex-row gap-2 sm:items-center">
            <UserPicker
              users={users}
              value={selectedUserId}
              onChange={setSelectedUserId}
              loading={loadingUsers}
            />
            <RangeSelect value={range} onChange={setRange} />
          </div>
        </div>

        {/* KPI Strip */}
        {visibleStats.length > 0 && (
          <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <KPI label="Commission (bonus inclusive)" value={fmtGBP(totals.commissionEarned)} />
            <KPI label="Bonus (sum)" value={fmtGBP(totals.bonusEarned)} />
            <KPI label="Standard Tickets" value={totals.standardTickets} />
            <KPI label="VIP Tickets" value={totals.vipTickets} />
          </div>
        )}
      </section>

      {/* Loading / Error / Empty */}
      {loadingStats && (
        <section className="grid gap-4 md:grid-cols-2">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="card animate-pulse">
              <div className="h-5 w-48 bg-white/5 rounded" />
              <div className="mt-3 h-4 w-full bg-white/5 rounded" />
              <div className="mt-2 h-4 w-2/3 bg-white/5 rounded" />
              <div className="mt-4 h-3 w-full bg-white/5 rounded" />
            </div>
          ))}
        </section>
      )}

      {error && !loadingStats && (
        <section className="card bg-red-500/10 border-red-500/20 text-red-100">{error}</section>
      )}

      {!loadingStats && !error && selectedUserId && visibleStats.length === 0 && (
        <section className="card text-slate-300">No stats available for the selected period.</section>
      )}

      {/* Monthly cards */}
      {!loadingStats && !error && visibleStats.length > 0 && (
        <section className="grid gap-4 md:grid-cols-2">
          {visibleStats.map((m) => (
            <MonthCard key={m.month} data={m} />
          ))}
        </section>
      )}
    </div>
  );
}

/* ---------------- Bits ---------------- */

function RangeSelect({ value, onChange }) {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="
          appearance-none rounded-xl border border-white/10 bg-white/[0.06]
          text-slate-100 px-3 py-2 pr-8
          focus:outline-none focus:ring-2 focus:ring-brand-600/40
        "
        title="Months to display"
      >
        <option className="bg-[var(--surface)] text-slate-100" value="6">Last 6 months</option>
        <option className="bg-[var(--surface)] text-slate-100" value="12">Last 12 months</option>
        <option className="bg-[var(--surface)] text-slate-100" value="all">All months</option>
      </select>
      <svg
        className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-300"
        viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"
      >
        <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z" clipRule="evenodd" />
      </svg>
    </div>
  );
}

function UserPicker({ users, value, onChange, loading }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [focusIdx, setFocusIdx] = useState(0);
  const rootRef = useRef(null);

  const selected = users.find((u) => String(u.id) === String(value));
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return users.slice(0, 50);
    return users.filter((u) =>
      [u.username, u.email, u.id].filter(Boolean).join(" ").toLowerCase().includes(needle)
    ).slice(0, 50);
  }, [users, q]);

  useEffect(() => {
    const onDown = (e) => {
      if (!open) return;
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, [open]);

  const pick = (id) => {
    onChange(String(id));
    setOpen(false);
  };

  const display = selected
    ? `${selected.username ?? selected.email ?? selected.id}${selected?.email ? ` (${selected.email})` : ""}`
    : loading
      ? "Loading users..."
      : "Select user…";

  return (
    <div className="relative z-[60]" ref={rootRef}>
      <button
        type="button"
        className="
          w-72 justify-between rounded-xl border border-white/10 bg-white/[0.06]
          text-left text-slate-100 px-3 py-2 flex items-center
          focus:outline-none focus:ring-2 focus:ring-brand-600/40
        "
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="truncate">{display}</span>
        <svg className="h-4 w-4 text-slate-300 ml-2" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z" clipRule="evenodd" />
        </svg>
      </button>

      {open && (
        <div className="absolute right-0 z-50 mt-2 w-[22rem] rounded-2xl border border-white/10 bg-[var(--surface)] p-2 shadow-card">
          <input
            autoFocus
            placeholder="Search by name or email…"
            value={q}
            onChange={(e) => { setQ(e.target.value); setFocusIdx(0); }}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown") { e.preventDefault(); setFocusIdx((i) => Math.min(i + 1, filtered.length - 1)); }
              if (e.key === "ArrowUp") { e.preventDefault(); setFocusIdx((i) => Math.max(i - 1, 0)); }
              if (e.key === "Enter" && filtered[focusIdx]) { pick(filtered[focusIdx].id); }
              if (e.key === "Escape") setOpen(false);
            }}
            className="input w-full"
          />

          <div className="mt-2 max-h-72 overflow-auto rounded-xl border border-white/10">
            {filtered.length === 0 ? (
              <div className="p-3 text-sm text-slate-400">No matches.</div>
            ) : (
              <ul role="listbox" className="divide-y divide-white/5">
                {filtered.map((u, idx) => {
                  const label = `${u.username ?? u.email ?? u.id}${u?.email ? ` (${u.email})` : ""}`;
                  const active = idx === focusIdx;
                  return (
                    <li
                      key={u.id}
                      role="option"
                      aria-selected={String(u.id) === String(value)}
                      onMouseEnter={() => setFocusIdx(idx)}
                      onClick={() => pick(u.id)}
                      className={`
                        cursor-pointer px-3 py-2 text-sm
                        ${active ? "bg-white/10 text-white" : "text-slate-200 hover:bg-white/5"}
                      `}
                    >
                      {label}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function KPI({ label, value }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
      <div className="text-[11px] uppercase tracking-wider text-slate-400">{label}</div>
      <div className="mt-1 text-xl font-semibold text-white">{value}</div>
    </div>
  );
}

function MonthCard({ data }) {
  const {
    month,
    totalTickets = 0,
    standardTickets = 0,
    vipTickets = 0,
    commissionEarned = 0,
    bonusEarned = 0,
    ticketsTowardGoal = 0,
    ticketsRemainingToGoal = 0,
  } = data || {};

  let label = "—";
  try {
    const [y, m] = String(month || "").split("-").map(Number);
    label = isFinite(y) && isFinite(m) ? format(new Date(y, m - 1), "MMMM yyyy") : "—";
  } catch {}

  const commissionRate = totalTickets >= 14 ? "20%" : "10%";
  const progress = Math.max(0, Math.min(100, (Number(ticketsTowardGoal || 0) / 14) * 100));

  return (
    <article className="card">
      <div className="flex items-start justify-between gap-2">
        <h3 className="text-xl font-semibold text-white">{label}</h3>
        <div className="flex flex-wrap gap-2">
          {bonusEarned > 0 && (
            <span className="badge bg-emerald-500/20 text-emerald-200">£200 bonus</span>
          )}
          <span className="badge bg-brand-600/20 text-slate-100">{commissionRate}</span>
        </div>
      </div>

      <div className="mt-3 grid gap-3 text-sm text-slate-300 sm:grid-cols-2">
        <Metric label="Standard Tickets" value={standardTickets} />
        <Metric label="VIP Tickets" value={vipTickets} />
        <Metric label="Total Tickets" value={totalTickets} />
        <Metric label="Commission Earned" value={fmtGBP(commissionEarned)} />
      </div>

      <div className="mt-4">
        <div className="flex items-center justify-between text-xs text-slate-400">
          <span>Progress to 14-ticket goal</span>
          <span>{ticketsTowardGoal}/14 ({Math.round(progress)}%)</span>
        </div>
        <div
          className="mt-1 h-2 w-full overflow-hidden rounded-full bg-white/10"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={14}
          aria-valuenow={ticketsTowardGoal}
        >
          <div
            className="h-full bg-gradient-to-r from-brand-600 to-glow transition-[width]"
            style={{ width: `${progress}%` }}
          />
        </div>

        {ticketsRemainingToGoal > 0 ? (
          <p className="mt-2 text-xs text-slate-400">
            {ticketsRemainingToGoal} more to hit the £200 bonus this month.
          </p>
        ) : (
          <p className="mt-2 text-xs text-emerald-300">Bonus target hit — nice!</p>
        )}
      </div>
    </article>
  );
}

function Metric({ label, value }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2">
      <div className="text-[11px] uppercase tracking-wider text-slate-400">{label}</div>
      <div className="mt-0.5 font-medium text-white">{value}</div>
    </div>
  );
}
